import {
  ReactElement, useCallback, useState,
} from 'react';
import { Flex } from 'reflexbox';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { apiCall } from 'providers';
import { ExceptionDut4, ScheduleDut4 } from '~/providers/types/api-private';
import { useStateVar } from 'helpers/useStateVar';
import { batchRequests } from 'helpers/batchRequests';
import { Loader } from 'components';
import { UnitStatusKey } from '~/providers/types/unit';

import { ScheduleEditCardDut4 } from '~/components/ScheduleEditCardDut4';
import { ScheduleViewCardDut4 } from '~/components/ScheduleViewCardDut4';
import { ExceptionEditCardDut4 } from '~/components/ExceptionEditCardDut4';
import { ExceptionViewCardDut4 } from '~/components/ExceptionViewCardDut4';
import { ExceptionsHeader } from '~/pages/Analysis/SchedulesModals/components/ExceptionsHeader';
import {
  saveSchedulesDut4,
  checkCardsNoConflictsDut4,
  saveExceptionsDut4,
  checkExceptsCardsConflictsDut4,
} from '~/pages/Analysis/DEVs/Dut4Services/editDev';
import { DutFilter, SelectedFilterDutOptions } from './MultipleDevProg/DutFilter';
import { DutTable } from './MultipleDevProg/DutTable';
import { SelectAllDevices } from './MultipleDevProg/components/SelectAllDevices';
import { ProgrammingProgress } from './MultipleDevProg/components/ProgrammingProgress';
import { DutList, DutError, DutOffline } from './MultipleDevProg/types';
import { parseDutList } from './MultipleDevProg/helpers/parseDevices';
import { sortDut } from './MultipleDevProg/helpers/sortDevices';
import {
  isAllDevicesSelected,
  toggleSelectAllDevices,
  verifyValueDefinedNotEmpty,
} from './MultipleDevProg/helpers/verifications';
import { limitDutScheduleDevices } from './MultipleDevProg/constants';
import {
  CustomSelect,
  Dut4ProgrammingCard,
  ProgrammingWrapper,
  ButtonPaddingWrapper,
  TabsContainer,
  TabHeaderRow,
  TabTopLeft,
  TabTopRight,
  TabButtonRow,
  TabNavButton,
  TabDivider,
  ScheduleListContainer,
  TotalCountLabel,
  AddButtonContainer,
  ExceptionsHeaderContainer,
  ExceptionItemWrapper,
  ScheduleItemWrapper,
  WideButton,
  SaveButton,
  StyledModalWindow,
} from './styles';
import { identifyDutIrCommands } from '~/helpers/dutIrCodes';

type IrCommand = { IR_ID: string; CMD_NAME: string | null; CMD_TYPE: string | null; TEMPER: number };

async function getDutIrCodesDut4(devId: string, dutsErrors: DutError[]): Promise<IrCommand[] | undefined> {
  let irCommands: IrCommand[] | undefined;
  await apiCall('/get-dut-ircodes-list', { devId })
    .then((response) => {
      irCommands = identifyDutIrCommands(response.list)
        .filter((command) => Number(command?.cmdName?.split(':')[1]) || null)
        .map((command) => ({
          IR_ID: command.IR_ID,
          CMD_NAME: command?.cmdName,
          CMD_TYPE: command?.CMD_TYPE,
          TEMPER: Number(command?.cmdName?.split(':')[1]),
        }))
        .sort((a, b) => a.TEMPER - b.TEMPER)
        .filter((value, index, self) => index === self.findIndex((c) => c.CMD_NAME === value.CMD_NAME));
    })
    .catch(() => {
      dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
    });
  return irCommands;
}

function checkDut4SchedulesIrCompatibility(devId: string, schedules: ScheduleDut4[], irCommands: IrCommand[], dutsErrors: DutError[]): void {
  for (const schedule of schedules) {
    if (schedule.DELETE || !schedule.SCHEDULE_STATUS) continue;

    const hasIr = (temper: number | undefined) => temper != null && !irCommands.find((cmd) => cmd.TEMPER === temper);

    if (schedule.CTRLOPER === '1_REFRIGERAR') {
      if (hasIr(schedule.SETPOINT)) {
        dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
        return;
      }
    } else if (schedule.CTRLOPER === '3_SOB_DEMANDA') {
      if ((schedule.SP_ACTION === 'COOL' || schedule.SP_ACTION === 'ECO') && schedule.SP_CMD?.startsWith('SET')) {
        const setpoint = Number(schedule.SP_CMD.slice(3));
        if (!Number.isNaN(setpoint) && !irCommands.find((cmd) => cmd.TEMPER === setpoint)) {
          dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
          return;
        }
      }
    } else if (schedule.CTRLOPER === '4_ECO') {
      if (hasIr(schedule.SETPOINT)
        || (schedule.LTC_CMD === 'COOL' && hasIr(schedule.LTC))
        || (schedule.LTI_CMD === 'COOL' && hasIr(schedule.LTI))) {
        dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
        return;
      }
    }
  }
}

function checkDut4ExceptionsIrCompatibility(devId: string, exceptions: ExceptionDut4[], irCommands: IrCommand[], dutsErrors: DutError[]): void {
  for (const exception of exceptions) {
    if (exception.DELETE) continue;

    const hasIr = (temper: number | undefined) => temper != null && !irCommands.find((cmd) => cmd.TEMPER === temper);

    if (exception.CTRLOPER === '1_REFRIGERAR') {
      if (hasIr(exception.SETPOINT)) {
        dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
        return;
      }
    } else if (exception.CTRLOPER === '3_SOB_DEMANDA') {
      if ((exception.SP_ACTION === 'COOL' || exception.SP_ACTION === 'ECO') && exception.SP_CMD?.startsWith('SET')) {
        const setpoint = Number(exception.SP_CMD.slice(3));
        if (!Number.isNaN(setpoint) && !irCommands.find((cmd) => cmd.TEMPER === setpoint)) {
          dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
          return;
        }
      }
    } else if (exception.CTRLOPER === '4_ECO') {
      if (hasIr(exception.SETPOINT)
        || (exception.LTC_CMD === 'COOL' && hasIr(exception.LTC))
        || (exception.LTI_CMD === 'COOL' && hasIr(exception.LTI))) {
        dutsErrors.push({ DUT_ID: devId, Motivo: 'codigoIrNaoCadastrado' });
        return;
      }
    }
  }
}

const DUT_TABLE_KEYS = {
  id: 'DEV_ID',
  name: 'ROOM_NAME',
  unitId: 'UNIT_ID',
  unitName: 'UNIT_NAME',
  cityName: 'CITY_NAME',
  stateName: 'STATE_NAME',
};

interface Dut4MultipleDevProgProps {
  clientId?: number;
  unitId?: number;
  statesListOpts?: {name: string, value: string}[];
  unitsListOpts?: {name: string, value: string, clientId: number}[];
  citiesListOpts?: {name: string, value: string}[];
}

export const Dut4MultipleDevProg = ({
  clientId,
  unitId,
  statesListOpts,
  unitsListOpts,
  citiesListOpts,
}: Dut4MultipleDevProgProps): ReactElement => {
  const { t } = useTranslation();

  const [state, render] = useStateVar({
    isFiltering: false,
    isSending: false,
    filteredDevList: [] as DutList[],
    openModal: null as string | null,
  });

  const [progressState, setProgressState] = useState({ currentDevices: 0, totalDevices: 0 });
  const [scheduleDut4, setScheduleDut4] = useState<ScheduleDut4[]>([]);
  const [scheduleDut4Deleted, setScheduleDut4Deleted] = useState<ScheduleDut4[]>([]);
  const [selectedScheduleDut4, setSelectedScheduleDut4] = useState<ScheduleDut4 | null>(null);
  const [selectedIndexScheduleDut4, setSelectedIndexScheduleDut4] = useState<number | null>(null);
  const [selectedAutomation, setSelectedAutomation] = useState<{ label: string; value: string } | null>(null);
  const [showExceptions, setShowExceptions] = useState(false);
  const [exceptionDut4, setExceptionDut4] = useState<ExceptionDut4[]>([]);
  const [exceptionDut4Deleted, setExceptionDut4Deleted] = useState<ExceptionDut4[]>([]);
  const [selectedExceptionDut4, setSelectedExceptionDut4] = useState<ExceptionDut4 | null>(null);
  const [selectedIndexExceptionDut4, setSelectedIndexExceptionDut4] = useState<number | null>(null);
  const [dut4ProgramacaoHabilitada, setDut4ProgramacaoHabilitada] = useState(true);
  const [dut4DefaultStatus, setDut4DefaultStatus] = useState<{ label: string; value: string } | null>(null);

  const automationOptions = [
    { label: t('habilitada'), value: 'IR' },
    { label: t('desabilitada'), value: 'DISABLED' },
  ];

  function clearSchedules() {
    setScheduleDut4Deleted((prev) => [
      ...prev,
      ...scheduleDut4.filter((s) => s.DUT_SCHEDULE_ID !== undefined).map((s) => ({ ...s, DELETE: true })),
    ]);
    setScheduleDut4([]);
  }

  function clearExceptions() {
    setExceptionDut4Deleted((prev) => [
      ...prev,
      ...exceptionDut4.filter((e) => e.DUT_EXCEPTION_ID !== undefined).map((e) => ({ ...e, DELETE: true })),
    ]);
    setExceptionDut4([]);
  }

  const callDutsFilter = useCallback(async (filterOptions: SelectedFilterDutOptions) => {
    if (!clientId && !unitId) return;

    state.isFiltering = true;
    render();

    try {
      const {
        cities, connections, dutAutConfigs, dutControlOperations,
        searchState, states, units, hasProgrammingMult, irCommandTemps, unitStatus,
      } = filterOptions;

      const unitIds = clientId
        ? verifyValueDefinedNotEmpty(units)?.map((unit) => Number(unit))
        : (unitId ? [unitId] : undefined);

      const { list: dutsList } = await apiCall('/dut/get-duts-list', {
        ...(clientId ? { clientIds: [clientId] } : { unitIds: [unitId] }),
        stateIds: verifyValueDefinedNotEmpty(states),
        cityIds: verifyValueDefinedNotEmpty(cities),
        status: verifyValueDefinedNotEmpty(connections),
        controlMode: verifyValueDefinedNotEmpty(dutControlOperations),
        automationConfig: verifyValueDefinedNotEmpty(dutAutConfigs),
        searchTerms: verifyValueDefinedNotEmpty(searchState)?.map(({ text }) => text.toLowerCase()),
        unitIds,
        hasProgrammingMult,
        irCommandTemps: verifyValueDefinedNotEmpty(irCommandTemps)?.map((temp) => Number(temp)),
        INCLUDE_UNIT_STATUS: verifyValueDefinedNotEmpty(unitStatus as UnitStatusKey[]),
        dutVersion: 'DUT4',
      });

      state.filteredDevList = dutsList.map((dut) => parseDutList(dut)).sort(sortDut);
    } catch (err) {
      console.error('Erro ao buscar lista de DUTs:', err);
    } finally {
      state.isFiltering = false;
      render();
    }
  }, [clientId, unitId, state, render]);

  const onClickSelectAllDevices = useCallback(() => {
    if (state.filteredDevList.length > 0) {
      toggleSelectAllDevices(state.filteredDevList);
      render();
    }
  }, [state, render]);

  function onSaveSchedule(schedule: ScheduleDut4) {
    const MAX_CARDS_PER_DAY = 6;
    const dayTranslationKeys: Record<keyof ScheduleDut4['DAYS'], string> = {
      mon: 'segundaFeira',
      tue: 'tercaFeira',
      wed: 'quartaFeira',
      thu: 'quintaFeira',
      fri: 'sextaFeira',
      sat: 'sabado',
      sun: 'domingo',
    };
    const otherSchedules = selectedIndexScheduleDut4 !== null
      ? scheduleDut4.filter((_, i) => i !== selectedIndexScheduleDut4)
      : scheduleDut4;
    for (const day of (Object.keys(schedule.DAYS) as (keyof ScheduleDut4['DAYS'])[])) {
      if (schedule.DAYS[day] && otherSchedules.filter((s) => s.DAYS[day]).length >= MAX_CARDS_PER_DAY) {
        toast.error(t('limiteProgramacoesPorDiaAtingido', { dia: t(dayTranslationKeys[day]) }));
        return;
      }
    }

    if (selectedIndexScheduleDut4 !== null) {
      const updated = [...scheduleDut4];
      updated[selectedIndexScheduleDut4] = schedule;
      setScheduleDut4(updated);
      setSelectedIndexScheduleDut4(null);
      setSelectedScheduleDut4(null);
    } else {
      setScheduleDut4((prev) => [...prev, schedule]);
    }
    state.openModal = null;
    render();
  }

  function onEditSchedule(cardPosition: number) {
    setSelectedScheduleDut4(scheduleDut4[cardPosition]);
    setSelectedIndexScheduleDut4(cardPosition);
    state.openModal = 'add-edit-schedule';
    render();
  }

  function onDeleteSchedule(scheduleId: number | undefined, cardPosition: number) {
    if (scheduleId !== undefined) {
      const deleted = scheduleDut4.find((s) => s.DUT_SCHEDULE_ID === scheduleId);
      if (deleted) setScheduleDut4Deleted((prev) => [...prev, { ...deleted, DELETE: true }]);
      setScheduleDut4((prev) => prev.filter((s) => s.DUT_SCHEDULE_ID !== scheduleId));
    } else {
      setScheduleDut4((prev) => prev.filter((_, i) => i !== cardPosition));
    }
    render();
  }

  function openAddScheduleModal() {
    setSelectedScheduleDut4(null);
    setSelectedIndexScheduleDut4(null);
    state.openModal = 'add-edit-schedule';
    render();
  }

  function openAddExceptionModal() {
    setSelectedExceptionDut4(null);
    setSelectedIndexExceptionDut4(null);
    state.openModal = 'add-edit-exception';
    render();
  }

  function onSaveException(exception: ExceptionDut4, cardIndex: number | null) {
    if (cardIndex !== null) {
      const updated = [...exceptionDut4];
      updated[cardIndex] = exception;
      setExceptionDut4(updated);
    } else {
      setExceptionDut4((prev) => [...prev, exception]);
    }
    state.openModal = null;
    render();
  }

  function onEditException(cardPosition: number) {
    setSelectedExceptionDut4(exceptionDut4[cardPosition]);
    setSelectedIndexExceptionDut4(cardPosition);
    state.openModal = 'add-edit-exception';
    render();
  }

  function onDeleteException(exceptionId: number | undefined, cardPosition: number) {
    if (exceptionId !== undefined) {
      const deleted = exceptionDut4.find((e) => e.DUT_EXCEPTION_ID === exceptionId);
      if (deleted) setExceptionDut4Deleted((prev) => [...prev, { ...deleted, DELETE: true }]);
      setExceptionDut4((prev) => prev.filter((e) => e.DUT_EXCEPTION_ID !== exceptionId));
    } else {
      setExceptionDut4((prev) => prev.filter((_, i) => i !== cardPosition));
    }
    render();
  }

  async function sendDut4Schedule() {
    const dutsErrors: DutError[] = [];
    const dutsOffline: DutOffline[] = [];

    const allSchedules = [...scheduleDut4, ...scheduleDut4Deleted];
    const allExceptions = [...exceptionDut4, ...exceptionDut4Deleted];

    const scheduleEnabled = selectedAutomation?.value === 'IR' && dut4ProgramacaoHabilitada;
    const useIr = selectedAutomation?.value === 'IR' ? 1 : 0;

    if (scheduleEnabled && !checkCardsNoConflictsDut4(allSchedules)) {
      return;
    }
    if (scheduleEnabled && !checkExceptsCardsConflictsDut4(allExceptions)) {
      return;
    }

    const selectedDevList = state.filteredDevList.filter((dev) => dev.checked);

    if (selectedDevList.length === 0) {
      toast.warn(t('semDispositivoSelecionad'));
      return;
    }

    for (const device of selectedDevList) {
      if (device.status === 'OFFLINE') {
        // dutsOffline.push({ DUT_ID: device.DEV_ID });
      }
    }

    const defaultStatusAutomation = (!scheduleEnabled
      ? 'NO_ACTION'
      : (dut4DefaultStatus?.value ?? 'OFF')) as 'OFF' | 'NO_ACTION';

    setProgressState({ currentDevices: 0, totalDevices: selectedDevList.length });
    state.isSending = true;
    render();
    toast.info(t('iniciadoProgramacaoEmMassa'));

    try {
      const promises = selectedDevList.map((dev) => async () => {
        const devErrors: DutError[] = [];

        if (!scheduleEnabled) {
          await apiCall('/dut/set-dut4-automation', {
            DEV_ID: dev.DEV_ID,
            DEFAULT_STATUS_AUTOMATION: defaultStatusAutomation,
            SCHEDULE_ENABLED: false,
            USE_IR: useIr,
          }).catch((err) => {
            dutsErrors.push({ DUT_ID: dev.DEV_ID, Motivo: t('erroProgDispositivos') });
          });
          setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          return;
        }

        const irCommands = await getDutIrCodesDut4(dev.DEV_ID, devErrors);

        if (devErrors.length === 0 && irCommands) {
          checkDut4SchedulesIrCompatibility(dev.DEV_ID, allSchedules, irCommands, devErrors);
          checkDut4ExceptionsIrCompatibility(dev.DEV_ID, allExceptions, irCommands, devErrors);
        }

        if (devErrors.length > 0) {
          dutsErrors.push(...devErrors);
          setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          return;
        }

        let existingSchedules: ScheduleDut4[] = [];
        await apiCall('/dut/get-dut-schedules', { DUT_ID: dev.DEV_ID, CLIENT_ID: dev.CLIENT_ID, UNIT_ID: dev.UNIT_ID })
          .then((response) => {
            existingSchedules = response.schedules as unknown as ScheduleDut4[];
          })
          .catch((err) => {
            console.error(err);
            devErrors.push({ DUT_ID: dev.DEV_ID, Motivo: 'erroBuscarCardsProgramacao' });
          });

        if (devErrors.length > 0) {
          dutsErrors.push(...devErrors);
          setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          return;
        }

        let existingExceptions: ExceptionDut4[] = [];
        if (allExceptions.length > 0) {
          await apiCall('/dut/get-dut-exceptions', { DUT_ID: dev.DEV_ID, CLIENT_ID: dev.CLIENT_ID, UNIT_ID: dev.UNIT_ID })
            .then((response) => {
              existingExceptions = response.exceptions as unknown as ExceptionDut4[];
            })
            .catch((err) => {
              console.error(err);
              devErrors.push({ DUT_ID: dev.DEV_ID, Motivo: 'erroBuscarCardsExcecao' });
            });

          if (devErrors.length > 0) {
            dutsErrors.push(...devErrors);
            setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
            return;
          }
        }

        const daysOfWeek = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

        const daysWithNewSchedules = new Set<string>();
        for (const schedule of allSchedules) {
          if (!schedule.DELETE && schedule.SCHEDULE_STATUS) {
            for (const day of daysOfWeek) {
              if (schedule.DAYS[day]) daysWithNewSchedules.add(day);
            }
          }
        }

        for (const existing of existingSchedules) {
          for (const day of daysOfWeek) {
            if (daysWithNewSchedules.has(day)) existing.DAYS[day] = false;
          }
        }

        const isAllDaysDisabled = (days: ScheduleDut4['DAYS']) => daysOfWeek.every((d) => !days[d]);

        const mergedSchedules: ScheduleDut4[] = [
          ...allSchedules,
          ...existingSchedules.map((s) => ({ ...s, DELETE: isAllDaysDisabled(s.DAYS) ? true : s.DELETE })),
        ];

        const MAX_CARDS_PER_DAY = 6;
        const activeAfterMerge = mergedSchedules.filter((s) => !s.DELETE);
        const dayLimitExceeded = daysOfWeek.some(
          (day) => activeAfterMerge.filter((s) => s.DAYS[day]).length > MAX_CARDS_PER_DAY,
        );
        if (dayLimitExceeded) {
          devErrors.push({ DUT_ID: dev.DEV_ID, Motivo: 'erroLimiteProgramacoesPorDia' });
          dutsErrors.push(...devErrors);
          setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          return;
        }

        await saveSchedulesDut4(dev.DEV_ID, dev.CLIENT_ID, dev.UNIT_ID, mergedSchedules)
          .then(async () => {
            if (allExceptions.length > 0) {
              const mergedExceptions = allExceptions.map((exception) => {
                if (exception.DELETE) return exception;
                const existing = existingExceptions.find((e) => e.EXCEPTION_DATE === exception.EXCEPTION_DATE);
                return existing ? { ...exception, DUT_EXCEPTION_ID: existing.DUT_EXCEPTION_ID } : exception;
              });
              await saveExceptionsDut4(dev.DEV_ID, dev.CLIENT_ID, dev.UNIT_ID, mergedExceptions);
            }
            await apiCall('/dut/set-dut4-automation', {
              DEV_ID: dev.DEV_ID,
              DEFAULT_STATUS_AUTOMATION: defaultStatusAutomation,
              SCHEDULE_ENABLED: true,
              USE_IR: useIr,
            }).catch((err) => {
              dutsErrors.push({ DUT_ID: dev.DEV_ID, Motivo: t('erroProgDispositivos') });
            });
            setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          })
          .catch((err) => {
            console.error(`Erro sendDut4Schedule ${dev.DEV_ID}: ${err.message}`);
            dutsErrors.push({ DUT_ID: dev.DEV_ID, Motivo: t('erroProgDispositivos') });
            setProgressState((prev) => ({ ...prev, currentDevices: prev.currentDevices + 1 }));
          });
      });

      await batchRequests(promises, limitDutScheduleDevices);

      if (dutsErrors.length > 0 || dutsOffline.length > 0) {
        throw new Error();
      }

      toast.success(t('progEnviadaSucesso'));
    } catch (err) {
      console.log(err);
      if (dutsErrors.length > 0) {
        const byMotivo = dutsErrors.reduce((acc, e) => {
          const key = e.Motivo ?? 'erroProgDispositivos';
          if (!acc[key]) acc[key] = [];
          acc[key].push(e.DUT_ID);
          return acc;
        }, {} as Record<string, string[]>);

        for (const [motivo, ids] of Object.entries(byMotivo)) {
          toast.error(`${t(motivo)}:\n${ids.join('\n')}`);
        }
      }
      if (dutsOffline.length > 0) {
        toast.error(`${t('dispositivosOffline')}:\n${dutsOffline.map((e) => e.DUT_ID).join('\n')}`);
      }
    } finally {
      state.isSending = false;
      render();
    }
  }

  const selectedDevCount = state.filteredDevList.filter((d) => d.checked).length;

  if (state.isSending) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Loader />
        <ProgrammingProgress
          currentDevices={progressState.currentDevices}
          totalDevices={progressState.totalDevices}
        />
      </Flex>
    );
  }

  return (
    <div>
      <DutFilter
        isFiltering={state.isFiltering}
        onClickFilter={callDutsFilter}
        clientId={clientId}
        unitId={unitId}
        statesListOpts={statesListOpts}
        unitsListOpts={unitsListOpts}
        citiesListOpts={citiesListOpts}
      />
      <Flex flexDirection="column" width="100%" mb="15px">
        <SelectAllDevices
          isAllSelected={isAllDevicesSelected(state.filteredDevList)}
          onClickSelect={onClickSelectAllDevices}
        />
      </Flex>
      <DutTable
        data={state.filteredDevList}
        keys={DUT_TABLE_KEYS}
        render={render}
        clientId={clientId}
        isLoading={state.isFiltering}
      />

      {selectedDevCount > 0 && (
        <>
          <ProgrammingWrapper>
            <Dut4ProgrammingCard>
              <CustomSelect
                options={automationOptions}
                value={selectedAutomation}
                placeholder={t('automacaoHabilitada')}
                onSelect={setSelectedAutomation}
                notNull
              />
              {selectedAutomation?.value === 'IR' && (
                <>
                  <CustomSelect
                    options={[
                      { label: t('desligar'), value: 'OFF' },
                      { label: t('semAcao'), value: 'NO_ACTION' },
                    ]}
                    value={!dut4ProgramacaoHabilitada ? { label: t('semAcao'), value: 'NO_ACTION' } : (dut4DefaultStatus ?? { label: t('desligar'), value: 'OFF' })}
                    placeholder={t('statusPadrao')}
                    onSelect={setDut4DefaultStatus}
                    notNull
                    disabled={!dut4ProgramacaoHabilitada}
                  />
                  <span
                    style={{
                      fontSize: '12px',
                      color: 'grey',
                      marginTop: '-10px',
                      marginBottom: '10px',
                      display: 'block',
                    }}
                  >
                    {t('statusPadraoForaDaProgramacao')}
                  </span>
                  <CustomSelect
                    options={[
                      { label: t('habilitada'), value: 'ENABLED' },
                      { label: t('desabilitada'), value: 'DISABLED' },
                    ]}
                    value={dut4ProgramacaoHabilitada
                      ? { label: t('habilitada'), value: 'ENABLED' }
                      : { label: t('desabilitada'), value: 'DISABLED' }}
                    placeholder={t('programacaoHabilitada')}
                    onSelect={(item) => {
                      const next = item.value === 'ENABLED';
                      setDut4DefaultStatus(next ? { label: t('desligar'), value: 'OFF' } : { label: t('semAcao'), value: 'NO_ACTION' });
                      setDut4ProgramacaoHabilitada(next);
                    }}
                    notNull
                  />
                  {dut4ProgramacaoHabilitada && (
                    <>
                      <Flex flexWrap="nowrap" flexDirection="row" alignItems="left">
                        <ButtonPaddingWrapper>
                          <WideButton
                            variant="primary"
                            onClick={() => {
                              if (!showExceptions) {
                                if (window.confirm('Deseja limpar todas as programações?')) {
                                  clearSchedules();
                                }
                              } else if (window.confirm('Deseja limpar todas as exceções?')) {
                                clearExceptions();
                              }
                            }}
                          >
                            {!showExceptions ? t('botaoLimparProgramacao') : t('botaoLimparExcecao')}
                          </WideButton>
                        </ButtonPaddingWrapper>
                      </Flex>
                      <TabsContainer>
                        <TabHeaderRow>
                          <TabTopLeft $isActive={!showExceptions} />
                          <span />
                          <TabTopRight $isActive={showExceptions} />
                          <span />
                        </TabHeaderRow>
                        <TabButtonRow>
                          <TabNavButton
                            type="button"
                            $isActive={!showExceptions}
                            onClick={() => { if (showExceptions) setShowExceptions(false); }}
                          >
                            {t('programacoes')}
                          </TabNavButton>
                          <TabDivider />
                          <TabNavButton
                            type="button"
                            $isActive={showExceptions}
                            onClick={() => { if (!showExceptions) setShowExceptions(true); }}
                          >
                            {t('excecoes')}
                          </TabNavButton>
                          <TabDivider />
                        </TabButtonRow>
                      </TabsContainer>
                      <ScheduleListContainer>
                        <Flex flexWrap="nowrap" flexDirection="row" alignItems="left">
                          <TotalCountLabel>
                            {`Total: ${!showExceptions ? scheduleDut4.length : exceptionDut4.length}`}
                          </TotalCountLabel>
                          <AddButtonContainer>
                            <WideButton
                              variant="primary"
                              onClick={() => { if (!showExceptions) openAddScheduleModal(); else openAddExceptionModal(); }}
                            >
                              {!showExceptions ? t('adicionarProgramacao') : t('adicionarExcecao')}
                            </WideButton>
                          </AddButtonContainer>
                        </Flex>
                        {showExceptions && exceptionDut4.length > 0 && (
                          <>
                            <ExceptionsHeaderContainer>
                              <ExceptionsHeader />
                            </ExceptionsHeaderContainer>
                            {exceptionDut4.map((exception, index) => (
                              <ExceptionItemWrapper key={exception.DUT_EXCEPTION_ID ?? index}>
                                <ExceptionViewCardDut4
                                  cardPosition={index}
                                  exception={exception}
                                  hideButtons={false}
                                  onHandleEdit={onEditException}
                                  onHandleDelete={onDeleteException}
                                />
                              </ExceptionItemWrapper>
                            ))}
                          </>
                        )}
                        {!showExceptions && (
                          <Flex flexWrap="nowrap" flexDirection="column" alignItems="left">
                            {scheduleDut4.map((schedule, index) => (index % 2 === 0
                              ? (
                                <ScheduleItemWrapper
                                  key={schedule.DUT_SCHEDULE_ID ?? index}
                                  $isFirst={index === 0}
                                >
                                  <ScheduleViewCardDut4
                                    cardPosition={index}
                                    schedule={schedule}
                                    hideButtons={false}
                                    onHandleEdit={onEditSchedule}
                                    onHandleDelete={onDeleteSchedule}
                                    irCommands={[]}
                                  />
                                  {index + 1 < scheduleDut4.length && (
                                    <ScheduleViewCardDut4
                                      cardPosition={index + 1}
                                      schedule={scheduleDut4[index + 1]}
                                      hideButtons={false}
                                      onHandleEdit={onEditSchedule}
                                      onHandleDelete={onDeleteSchedule}
                                      irCommands={[]}
                                    />
                                  )}
                                </ScheduleItemWrapper>
                              )
                              : <span key={schedule.DUT_SCHEDULE_ID} />
                            ))}
                          </Flex>
                        )}
                      </ScheduleListContainer>
                    </>
                  )}
                </>
              )}
            </Dut4ProgrammingCard>
          </ProgrammingWrapper>
          {selectedAutomation != null && (
            <ProgrammingWrapper>
              <SaveButton
                disabled={state.isSending}
                onClick={sendDut4Schedule}
                variant="primary"
              >
                {t('botaoSalvar')}
              </SaveButton>
            </ProgrammingWrapper>
          )}
        </>
      )}

      {state.openModal === 'add-edit-schedule' && (
        <StyledModalWindow
          onClickOutside={() => { state.openModal = null; render(); }}
          id="schedule-modal"
        >
          <ScheduleEditCardDut4
            action={selectedIndexScheduleDut4 !== null ? 'edit' : 'create'}
            schedule={selectedScheduleDut4}
            irCommands={[]}
            isMultipleDevProg
            onHandleSave={onSaveSchedule}
            onHandleCancel={() => { state.openModal = null; render(); }}
          />
        </StyledModalWindow>
      )}
      {state.openModal === 'add-edit-exception' && (
        <StyledModalWindow
          onClickOutside={() => { state.openModal = null; render(); }}
          id="exception-modal"
        >
          <ExceptionEditCardDut4
            action={selectedIndexExceptionDut4 !== null ? 'edit' : 'create'}
            exception={selectedExceptionDut4}
            irCommands={[]}
            isMultipleDevProg
            cardIndex={selectedIndexExceptionDut4}
            onHandleSave={onSaveException}
            onHandleCancel={() => { state.openModal = null; render(); }}
          />
        </StyledModalWindow>
      )}
    </div>
  );
};
